import * as React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, RefreshCw, Trash2, TriangleAlert, X } from 'lucide-react';
import {
  DocLang,
  PROFILE_FACT_LABELS_DE,
  PROFILE_FACT_LABELS_EN,
  PROFILE_MAX_PAGES,
  PROFILE_MAX_PAGES_OVERRIDE,
  todayIso,
  type DocLang as DocLangCode,
  type ProfileContent,
} from '@clawhire/shared';
import { api, isApiError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { SkeletonLines } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import { PdfFrame } from './PdfFrame';
import { PositionPicker } from './PositionPicker';
import {
  base64ToBlob,
  candidatePath,
  formatLanguages,
  toCheckLists,
  useBlobObjectUrl,
  versionTag,
} from './api';
import type {
  CandidateDetail,
  CheckLists,
  PositionRef,
  ProfileGenerateResponse,
  ProfilePreviewResponse,
} from './types';

export interface NewProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidate: CandidateDetail;
  isAdmin: boolean;
  onSaved: () => void;
}

type Project = NonNullable<ProfileContent['projects']>[number];
type Education = NonNullable<ProfileContent['education']>[number];
type Certification = NonNullable<ProfileContent['certifications']>[number];

interface Preview {
  blob: Blob;
  pageCount: number | null;
  checks: CheckLists;
}

/** The prefill endpoint may return the content directly or wrapped as `{ content }`. */
function extractContent(raw: unknown): ProfileContent | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as { content?: unknown; candidate?: unknown };
  if (o.content && typeof o.content === 'object' && (o.content as { candidate?: unknown }).candidate) {
    return o.content as ProfileContent;
  }
  if (o.candidate && typeof o.candidate === 'object') return raw as ProfileContent;
  return null;
}

/** Local draft when the prefill endpoint is unavailable: what the record knows, nothing invented. */
function emptyDraft(candidate: CandidateDetail, lang: DocLangCode): ProfileContent {
  const labels = lang === 'DE' ? PROFILE_FACT_LABELS_DE : PROFILE_FACT_LABELS_EN;
  const location = [candidate.city, candidate.country].filter(Boolean).join(', ');
  const languages = candidate.languages.length ? formatLanguages(candidate.languages) : '';
  const availability = candidate.availableFrom ? formatDate(candidate.availableFrom) : '';
  const values = ['', location, languages, availability];
  return {
    lang: lang === 'DE' ? 'de' : 'en',
    ref: candidate.candidateId,
    date: todayIso(),
    candidate: {
      displayName: candidate.computed.displayName,
      title: candidate.headline ?? candidate.label ?? '',
      headline: '',
      facts: labels.map((label, i) => ({ label, value: values[i] ?? '' })),
    },
    assessment: { intro: '', bullets: [], signoff: '' },
    summary: '',
    skills: candidate.skills.slice(0, 10).map((s) => [s, ''] as [string, string]),
    projects: [],
    education: [],
    certifications: [],
  };
}

function summaryToText(summary: ProfileContent['summary']): string {
  if (!summary) return '';
  return Array.isArray(summary) ? summary.join('\n') : summary;
}

function linesToArray(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

/**
 * Full-screen profile generator: position (or generic) + language → prefill → editable
 * ProfileContent form → "Generate & preview" (PDF + checks) → "Save version" with the
 * CHECKS_FAILED handling (admin force with a reason, optional 3-page override).
 */
export function NewProfileDialog({ open, onOpenChange, candidate, isAdmin, onSaved }: NewProfileDialogProps) {
  const proposedPositions = React.useMemo<PositionRef[]>(
    () => candidate.submissions.map((s) => s.position),
    [candidate.submissions],
  );
  const [generic, setGeneric] = React.useState(proposedPositions.length === 0);
  const [position, setPosition] = React.useState<PositionRef | null>(proposedPositions[0] ?? null);
  const [lang, setLang] = React.useState<DocLangCode>('EN');
  const [content, setContent] = React.useState<ProfileContent | null>(null);
  const [dirty, setDirty] = React.useState(false);
  const [preview, setPreview] = React.useState<Preview | null>(null);
  const [changeNote, setChangeNote] = React.useState('');
  const [failed, setFailed] = React.useState<CheckLists | null>(null);
  const [forceReason, setForceReason] = React.useState('');
  const [allowExtraPage, setAllowExtraPage] = React.useState(false);
  const previewUrl = useBlobObjectUrl(preview?.blob ?? null);
  const ids = { note: React.useId(), force: React.useId(), extra: React.useId(), generic: React.useId() };

  React.useEffect(() => {
    if (open) {
      setGeneric(proposedPositions.length === 0);
      setPosition(proposedPositions[0] ?? null);
      setLang('EN');
      setContent(null);
      setDirty(false);
      setPreview(null);
      setChangeNote('');
      setFailed(null);
      setForceReason('');
      setAllowExtraPage(false);
    }
  }, [open, proposedPositions]);

  const positionId = generic ? null : (position?.id ?? null);
  const prefill = useQuery({
    queryKey: ['candidates', 'profile-prefill', candidate.id, positionId, lang],
    queryFn: () =>
      api.get<unknown>(`${candidatePath(candidate.id)}/profile/prefill`, {
        query: { position: positionId ?? undefined, lang },
      }),
    enabled: open && (generic || Boolean(positionId)),
    staleTime: 0,
    retry: false,
  });

  React.useEffect(() => {
    if (!open || dirty) return;
    if (prefill.data !== undefined) {
      const c = extractContent(prefill.data);
      setContent(c ?? emptyDraft(candidate, lang));
    } else if (prefill.isError) {
      setContent(emptyDraft(candidate, lang));
    }
  }, [open, dirty, prefill.data, prefill.isError, candidate, lang]);

  const patch = (fn: (c: ProfileContent) => ProfileContent) => {
    setContent((c) => (c ? fn(c) : c));
    setDirty(true);
    setPreview(null);
    setFailed(null);
  };

  const reload = () => {
    setDirty(false);
    setPreview(null);
    setFailed(null);
    void prefill.refetch();
  };

  const generatePreview = useMutation({
    mutationFn: () =>
      api.post<ProfilePreviewResponse>(`${candidatePath(candidate.id)}/profile/preview`, {
        lang,
        content,
        position: positionId ?? undefined,
      }),
    onSuccess: (res) => {
      setPreview({
        blob: base64ToBlob(res.pdfBase64),
        pageCount: res.pageCount ?? null,
        checks: toCheckLists(res.checks),
      });
      setFailed(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const save = useMutation({
    mutationFn: (force: boolean) =>
      api.post<ProfileGenerateResponse>(`${candidatePath(candidate.id)}/profile/generate`, {
        position: positionId ?? undefined,
        lang,
        content,
        changeNote: changeNote.trim() || undefined,
        force: force || undefined,
        forceReason: force ? forceReason.trim() : undefined,
        allowExtraPage: isAdmin && allowExtraPage ? true : undefined,
      }),
    onSuccess: (res) => {
      const v =
        res.version && typeof res.version === 'object'
          ? (res.version as { version?: number }).version
          : undefined;
      toast.success(v ? `Profile ${versionTag(v)} saved` : 'Profile saved');
      const warnings = toCheckLists(res.checks).warnings;
      for (const w of warnings) toast.warning(w.message);
      onSaved();
    },
    onError: (e: Error) => {
      if (isApiError(e) && e.status === 409 && e.code === 'CHECKS_FAILED') {
        const lists = toCheckLists(e.details);
        if (lists.errors.length === 0)
          lists.errors.push({ code: 'CHECKS_FAILED', level: 'error', message: e.message });
        setFailed(lists);
        return;
      }
      toast.error(e.message);
    },
  });

  const close = () => {
    if (dirty && !window.confirm('Discard the unsaved profile draft?')) return;
    onOpenChange(false);
  };

  const maxPages = isAdmin && allowExtraPage ? PROFILE_MAX_PAGES_OVERRIDE : PROFILE_MAX_PAGES;
  const canGenerate =
    Boolean(content) && (generic || Boolean(position)) && !generatePreview.isPending && !save.isPending;
  const de = lang === 'DE';

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent
        hideClose
        className="flex h-[100dvh] w-screen max-w-none flex-col gap-0 rounded-none border-0 p-0 sm:rounded-none"
      >
        <header className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
          <div className="min-w-0">
            <DialogTitle className="truncate">New profile · {candidate.computed.displayName}</DialogTitle>
            <DialogDescription className="truncate">
              {candidate.candidateId} ·{' '}
              {generic
                ? 'generic profile'
                : position
                  ? `${position.requestNo} ${position.role}`
                  : 'choose a position'}{' '}
              · {DocLang.label(lang)}
            </DialogDescription>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => generatePreview.mutate()}
              disabled={!canGenerate}
              loading={generatePreview.isPending}
            >
              Generate &amp; preview
            </Button>
            <Button
              onClick={() => save.mutate(false)}
              disabled={!canGenerate}
              loading={save.isPending && !failed}
            >
              Save version
            </Button>
            <Button variant="ghost" size="icon" onClick={close} aria-label="Close">
              <X aria-hidden="true" />
            </Button>
          </div>
        </header>

        <div className="grid min-h-0 flex-1 lg:grid-cols-2">
          <div className="scrollbar-thin min-h-0 space-y-6 overflow-y-auto p-4">
            <section className="space-y-3" aria-label="Target">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id={ids.generic}
                    checked={generic}
                    onCheckedChange={(v) => {
                      setGeneric(v);
                      setDirty(false);
                      setPreview(null);
                    }}
                  />
                  <Label htmlFor={ids.generic} className="font-normal">
                    Generic profile (no position)
                  </Label>
                </div>
                <div
                  role="group"
                  aria-label="Language"
                  className="inline-flex h-9 items-center rounded-md border p-0.5"
                >
                  {DocLang.codes.map((code) => (
                    <Button
                      key={code}
                      type="button"
                      variant={lang === code ? 'secondary' : 'ghost'}
                      size="sm"
                      className="h-7"
                      aria-pressed={lang === code}
                      onClick={() => {
                        setLang(code);
                        setPreview(null);
                      }}
                    >
                      {DocLang.label(code)}
                    </Button>
                  ))}
                </div>
                <Button variant="ghost" size="sm" onClick={reload} disabled={prefill.isFetching}>
                  <RefreshCw aria-hidden="true" className={cn(prefill.isFetching && 'animate-spin')} /> Reload
                  draft
                </Button>
              </div>
              {generic ? null : (
                <PositionPicker
                  value={position}
                  onChange={(p) => {
                    setPosition(p);
                    setDirty(false);
                    setPreview(null);
                  }}
                  suggested={proposedPositions}
                  suggestedLabel="Proposed for"
                />
              )}
              {prefill.isError ? (
                <p className="text-xs text-warning">
                  Draft could not be loaded (
                  {prefill.error instanceof Error ? prefill.error.message : 'error'}); starting from the
                  record.
                </p>
              ) : null}
            </section>

            {content ? <ProfileForm content={content} patch={patch} de={de} /> : <SkeletonLines lines={8} />}
          </div>

          <div className="scrollbar-thin min-h-0 space-y-4 overflow-y-auto border-t p-4 lg:border-l lg:border-t-0">
            <section aria-label="Preview" className="space-y-2">
              <h3 className="text-sm font-semibold">Preview</h3>
              {preview ? (
                <>
                  <p
                    className={cn(
                      'text-sm',
                      preview.pageCount !== null &&
                        preview.pageCount > maxPages &&
                        'font-medium text-destructive',
                    )}
                  >
                    {preview.pageCount === null
                      ? 'Page count unknown'
                      : `${preview.pageCount} page${preview.pageCount === 1 ? '' : 's'}`}{' '}
                    · limit {maxPages}
                  </p>
                  <ChecksList checks={preview.checks} />
                  <PdfFrame
                    src={previewUrl}
                    title="Profile preview"
                    loading={!previewUrl}
                    className="h-[60vh]"
                  />
                </>
              ) : generatePreview.isPending ? (
                <SkeletonLines lines={4} />
              ) : (
                <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                  Generate a preview to see the PDF and the content checks (forbidden terms, PII, rates,
                  languages, page limit).
                </p>
              )}
            </section>

            <section aria-label="Save" className="space-y-3 rounded-md border p-3">
              <div className="space-y-1.5">
                <Label htmlFor={ids.note}>Change note</Label>
                <Input
                  id={ids.note}
                  value={changeNote}
                  onChange={(e) => setChangeNote(e.target.value)}
                  placeholder="What changed in this version"
                  maxLength={300}
                />
              </div>
              {failed ? (
                <div
                  role="alert"
                  className="space-y-2 rounded-md border border-destructive/40 bg-destructive/10 p-3"
                >
                  <p className="text-sm font-medium text-destructive">
                    Checks failed – the version was not saved.
                  </p>
                  <ChecksList checks={failed} />
                  {isAdmin ? (
                    <div className="space-y-2 pt-1">
                      <div className="space-y-1.5">
                        <Label htmlFor={ids.force}>Reason for saving anyway (admin)</Label>
                        <Textarea
                          id={ids.force}
                          value={forceReason}
                          onChange={(e) => setForceReason(e.target.value)}
                          rows={2}
                          placeholder="Why the errors are acceptable in this case"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={ids.extra}
                          checked={allowExtraPage}
                          onCheckedChange={(v) => setAllowExtraPage(v === true)}
                        />
                        <Label htmlFor={ids.extra} className="font-normal">
                          Allow {PROFILE_MAX_PAGES_OVERRIDE} pages
                        </Label>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={forceReason.trim().length < 3 || save.isPending}
                        loading={save.isPending}
                        onClick={() => save.mutate(true)}
                      >
                        Save anyway (force)
                      </Button>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Fix the errors above or ask an admin to force-save.
                    </p>
                  )}
                </div>
              ) : null}
            </section>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChecksList({ checks }: { checks: CheckLists }) {
  if (checks.errors.length === 0 && checks.warnings.length === 0) {
    return <p className="text-sm text-success">All checks passed.</p>;
  }
  return (
    <ul className="space-y-1 text-sm" aria-label="Content checks">
      {checks.errors.map((c, i) => (
        <li key={`e-${i}`} className="flex items-start gap-2 text-destructive">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {c.message}
            {c.path ? <span className="text-xs text-muted-foreground"> ({c.path})</span> : null}
          </span>
        </li>
      ))}
      {checks.warnings.map((c, i) => (
        <li key={`w-${i}`} className="flex items-start gap-2 text-warning">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {c.message}
            {c.path ? <span className="text-xs text-muted-foreground"> ({c.path})</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Form bound to ProfileContent

function ProfileForm({
  content,
  patch,
  de,
}: {
  content: ProfileContent;
  patch: (fn: (c: ProfileContent) => ProfileContent) => void;
  de: boolean;
}) {
  const summaryWasArray = Array.isArray(content.summary);
  return (
    <div className="space-y-6">
      <FormSection title="Candidate">
        <Field label="Display name" hint="Fixed: first name + initial, or the candidate ID.">
          <Input value={content.candidate.displayName} readOnly disabled />
        </Field>
        <Field label="Title">
          <Input
            value={content.candidate.title}
            onChange={(e) => patch((c) => ({ ...c, candidate: { ...c.candidate, title: e.target.value } }))}
            maxLength={120}
          />
        </Field>
        <Field label="Headline">
          <Textarea
            value={content.candidate.headline ?? ''}
            onChange={(e) =>
              patch((c) => ({ ...c, candidate: { ...c.candidate, headline: e.target.value } }))
            }
            rows={2}
            maxLength={400}
          />
        </Field>
        <div className="space-y-2">
          <p className="text-sm font-medium">Facts</p>
          {content.candidate.facts.map((fact, i) => (
            <div key={i} className="grid grid-cols-[8rem_1fr] items-center gap-2">
              <Input
                value={fact.label}
                readOnly
                disabled
                aria-label={`Fact ${i + 1} label`}
                className="h-8"
              />
              <Input
                value={fact.value}
                onChange={(e) =>
                  patch((c) => ({
                    ...c,
                    candidate: {
                      ...c.candidate,
                      facts: c.candidate.facts.map((f, j) => (j === i ? { ...f, value: e.target.value } : f)),
                    },
                  }))
                }
                aria-label={`${fact.label} value`}
                className="h-8"
                maxLength={220}
              />
            </div>
          ))}
        </div>
      </FormSection>

      <FormSection title={de ? 'Einschätzung' : 'Assessment'}>
        <Field label="Intro">
          <Textarea
            value={content.assessment?.intro ?? ''}
            onChange={(e) =>
              patch((c) => ({ ...c, assessment: { ...(c.assessment ?? {}), intro: e.target.value } }))
            }
            rows={3}
            maxLength={600}
          />
        </Field>
        <BulletsEditor
          label="Bullets (3–5)"
          value={content.assessment?.bullets ?? []}
          max={6}
          onChange={(bullets) => patch((c) => ({ ...c, assessment: { ...(c.assessment ?? {}), bullets } }))}
        />
        <Field label="Sign-off">
          <Textarea
            value={content.assessment?.signoff ?? ''}
            onChange={(e) =>
              patch((c) => ({ ...c, assessment: { ...(c.assessment ?? {}), signoff: e.target.value } }))
            }
            rows={2}
            maxLength={300}
          />
        </Field>
      </FormSection>

      <FormSection title="Summary">
        <Field label={summaryWasArray ? 'Summary (one paragraph per line)' : 'Summary'}>
          <Textarea
            value={summaryToText(content.summary)}
            onChange={(e) =>
              patch((c) => ({
                ...c,
                summary: summaryWasArray ? linesToArray(e.target.value) : e.target.value,
              }))
            }
            rows={5}
          />
        </Field>
      </FormSection>

      <FormSection title="Skills (max 10)">
        <div className="space-y-2">
          {(content.skills ?? []).map(([name, detail], i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={name}
                onChange={(e) =>
                  patch((c) => ({
                    ...c,
                    skills: (c.skills ?? []).map((s, j) => (j === i ? [e.target.value, s[1]] : s)),
                  }))
                }
                placeholder="Skill"
                aria-label={`Skill ${i + 1} name`}
                className="h-8 w-1/3"
              />
              <Input
                value={detail}
                onChange={(e) =>
                  patch((c) => ({
                    ...c,
                    skills: (c.skills ?? []).map((s, j) => (j === i ? [s[0], e.target.value] : s)),
                  }))
                }
                placeholder="Detail (years, level, tools)"
                aria-label={`Skill ${i + 1} detail`}
                className="h-8 flex-1"
              />
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Remove skill ${name || i + 1}`}
                onClick={() => patch((c) => ({ ...c, skills: (c.skills ?? []).filter((_, j) => j !== i) }))}
              >
                <Trash2 aria-hidden="true" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            disabled={(content.skills ?? []).length >= 10}
            onClick={() => patch((c) => ({ ...c, skills: [...(c.skills ?? []), ['', '']] }))}
          >
            <Plus aria-hidden="true" /> Add skill
          </Button>
        </div>
      </FormSection>

      <FormSection title="Projects (max 8)">
        <div className="space-y-4">
          {(content.projects ?? []).map((p, i) => (
            <ProjectEditor
              key={i}
              index={i}
              project={p}
              total={(content.projects ?? []).length}
              onChange={(next) =>
                patch((c) => ({ ...c, projects: (c.projects ?? []).map((x, j) => (j === i ? next : x)) }))
              }
              onRemove={() =>
                patch((c) => ({ ...c, projects: (c.projects ?? []).filter((_, j) => j !== i) }))
              }
              onMove={(dir) =>
                patch((c) => {
                  const list = [...(c.projects ?? [])];
                  const to = i + dir;
                  if (to < 0 || to >= list.length) return c;
                  [list[i], list[to]] = [list[to]!, list[i]!];
                  return { ...c, projects: list };
                })
              }
            />
          ))}
          <Button
            variant="outline"
            size="sm"
            disabled={(content.projects ?? []).length >= 8}
            onClick={() =>
              patch((c) => ({
                ...c,
                projects: [
                  ...(c.projects ?? []),
                  { role: '', client: '', period: '', context: '', bullets: [], stack: '' },
                ],
              }))
            }
          >
            <Plus aria-hidden="true" /> Add project
          </Button>
        </div>
      </FormSection>

      <FormSection title="Education">
        <TripleRows
          rows={content.education ?? []}
          labels={['Title', 'Institution', 'Year']}
          keys={['title', 'institution', 'year']}
          onChange={(rows) => patch((c) => ({ ...c, education: rows as Education[] }))}
          addLabel="Add education"
        />
      </FormSection>

      <FormSection title="Certifications">
        <TripleRows
          rows={content.certifications ?? []}
          labels={['Title', 'Issuer', 'Year']}
          keys={['title', 'issuer', 'year']}
          onChange={(rows) => patch((c) => ({ ...c, certifications: rows as Certification[] }))}
          addLabel="Add certification"
        />
      </FormSection>
    </div>
  );
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3" aria-label={title}>
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactElement }) {
  const id = React.useId();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {React.cloneElement(children, { id })}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function BulletsEditor({
  label,
  value,
  max,
  onChange,
}: {
  label: string;
  value: string[];
  max: number;
  onChange: (bullets: string[]) => void;
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{label}</p>
      {value.map((b, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={b}
            onChange={(e) => onChange(value.map((x, j) => (j === i ? e.target.value : x)))}
            aria-label={`${label} ${i + 1}`}
            className="h-8"
          />
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Remove bullet ${i + 1}`}
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          >
            <Trash2 aria-hidden="true" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        disabled={value.length >= max}
        onClick={() => onChange([...value, ''])}
      >
        <Plus aria-hidden="true" /> Add bullet
      </Button>
    </div>
  );
}

function ProjectEditor({
  index,
  project,
  total,
  onChange,
  onRemove,
  onMove,
}: {
  index: number;
  project: Project;
  total: number;
  onChange: (p: Project) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const set = (patch: Partial<Project>) => onChange({ ...project, ...patch });
  return (
    <fieldset className="space-y-2 rounded-md border p-3">
      <legend className="px-1 text-xs font-medium text-muted-foreground">Project {index + 1}</legend>
      <div className="grid gap-2 sm:grid-cols-2">
        <Input
          value={project.role}
          onChange={(e) => set({ role: e.target.value })}
          placeholder="Role"
          aria-label={`Project ${index + 1} role`}
          className="h-8"
          maxLength={120}
        />
        <Input
          value={project.client ?? ''}
          onChange={(e) => set({ client: e.target.value })}
          placeholder="Client (anonymised: industry, size, region)"
          aria-label={`Project ${index + 1} client`}
          className="h-8"
          maxLength={160}
        />
        <Input
          value={project.period ?? ''}
          onChange={(e) => set({ period: e.target.value })}
          placeholder="Period, e.g. 03/2024 – 06/2026"
          aria-label={`Project ${index + 1} period`}
          className="h-8"
          maxLength={60}
        />
        <Input
          value={project.stack ?? ''}
          onChange={(e) => set({ stack: e.target.value })}
          placeholder="Stack"
          aria-label={`Project ${index + 1} stack`}
          className="h-8"
          maxLength={400}
        />
      </div>
      <Textarea
        value={project.context ?? ''}
        onChange={(e) => set({ context: e.target.value })}
        placeholder="Context"
        aria-label={`Project ${index + 1} context`}
        rows={2}
        maxLength={600}
      />
      <Textarea
        value={(project.bullets ?? []).join('\n')}
        onChange={(e) => set({ bullets: e.target.value.split('\n').slice(0, 6) })}
        onBlur={(e) => set({ bullets: linesToArray(e.target.value).slice(0, 6) })}
        placeholder="Achievements, one per line (max 6)"
        aria-label={`Project ${index + 1} bullets`}
        rows={3}
      />
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Move project ${index + 1} up`}
          disabled={index === 0}
          onClick={() => onMove(-1)}
        >
          <ArrowUp aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Move project ${index + 1} down`}
          disabled={index === total - 1}
          onClick={() => onMove(1)}
        >
          <ArrowDown aria-hidden="true" />
        </Button>
        <Button variant="ghost" size="sm" className="ml-auto" onClick={onRemove}>
          <Trash2 aria-hidden="true" /> Remove
        </Button>
      </div>
    </fieldset>
  );
}

type TripleRow = Record<string, string | undefined>;

function TripleRows({
  rows,
  labels,
  keys,
  onChange,
  addLabel,
}: {
  rows: TripleRow[];
  labels: [string, string, string];
  keys: [string, string, string];
  onChange: (rows: TripleRow[]) => void;
  addLabel: string;
}) {
  return (
    <div className="space-y-2">
      {rows.map((row, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_5rem_auto] items-center gap-2">
          {keys.map((k, ki) => (
            <Input
              key={k}
              value={row[k] ?? ''}
              onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)))}
              placeholder={labels[ki]}
              aria-label={`${labels[ki]} ${i + 1}`}
              className="h-8"
            />
          ))}
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Remove row ${i + 1}`}
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
          >
            <Trash2 aria-hidden="true" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...rows, { [keys[0]]: '' }])}>
        <Plus aria-hidden="true" /> {addLabel}
      </Button>
    </div>
  );
}
